import React, { useState, useEffect} from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  fetchCompanies,
  fetchDepartments,
  fetchJobFunctions,
  createEmployee,
  updateEmployee,
  checkEmailAvailability,
  checkIdentityAvailability,
  CATEGORY_OPTIONS,
  IDENTITY_TYPE_OPTIONS,
  type CompanyOption,
  type DepartmentOption,
  type JobFunctionOption,
  type EmployeeDetail,
} from '../api/employees';

// Liste des nationalités africaines
const AFRICAN_NATIONALITIES = [
  'Ivoirienne',
  'Sénégalaise',
  'Malienne',
  'Burkinabè',
  'Nigérienne',
  'Togolaise',
  'Béninoise',
  'Ghanéenne',
  'Nigériane',
  'Camerounaise',
  'Congolaise (RDC)',
  'Congolaise (RC)',
  'Gabonaise',
  'Tchadienne',
  'Centrafricaine',
  'Guinéenne',
  'Bissau-Guinéenne',
  'Équato-guinéenne',
  'Mauritanienne',
  'Gambienne',
  'Sierra-léonaise',
  'Libérienne',
  'Sud-africaine',
  'Kényane',
  'Tanzanienne',
  'Ougandaise',
  'Rwandaise',
  'Burundaise',
  'Éthiopienne',
  'Somalienne',
  'Djiboutienne',
  'Érythréenne',
  'Soudanaise',
  'Sud-soudanaise',
  'Égyptienne',
  'Libyenne',
  'Tunisienne',
  'Algérienne',
  'Marocaine',
  'Mauricienne',
  'Malgache',
  'Comorienne',
  'Seychelloise',
  'Zambienne',
  'Zimbabwéenne',
  'Botswanaise',
  'Namibienne',
  'Mozambicaine',
  'Malawite',
  'Angolaise',
  'São-toméenne',
  'Cap-verdienne',
  'Autre',
];

interface EmployeeFormProps {
  mode?: 'create' | 'edit';
  employeeData?: EmployeeDetail;
  onSuccess?: () => void;
}

const EmployeeForm: React.FC<EmployeeFormProps> = ({ 
  mode = 'create', 
  employeeData,
  onSuccess 
}) => {
  const navigate = useNavigate();
  
  // États pour tous les champs du formulaire
  const [formData, setFormData] = useState({
    // Informations personnelles
    nomPrenom: '',
    dateNaissance: '',
    lieuNaissance: '',
    nationalite: 'Ivoirienne',
    pere: '',
    mere: '',
    domicile: '',
    naturePiece: 'CNI' as 'CNI' | 'PASSEPORT' | 'PERMIS' | 'AUTRE',
    numeroPiece: '',
    dateEtablissementPiece: '',
    validitePiece: '',
    numeroCNPS: '',
    gender: 'M' as 'M' | 'F',
    phone: '',
    phoneEmergency: '',
    city: 'Abidjan',

    // Détails du contrat
    typeContrat: 'CDI' as 'CDI' | 'CDD' | 'STAGE' | 'PRESTATION',
    dateSignature: '',
    dateDebut: '',
    dateFin: '',
    jobFunctionId: '' as number | '',
    categorieProfessionnelle: '' as (typeof CATEGORY_OPTIONS)[number]['value'] | '',
    lieuTravail: '',

    // Organisation et contact
    departementId: '' as number | '',
    societeId: '' as number | '',
    email: '',

    // Rémunération
    salaireMensuel: 0,
    primeTransport: 0,
    primeRendement: 0,
    primeCommunication: 0,
    surSalaire: 0,
    salaireCategoriel: 0,
    totalSalaireNet: 0,
  });

  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [jobFunctions, setJobFunctions] = useState<JobFunctionOption[]>([]);
  const [companyInput, setCompanyInput] = useState('');
  const [departmentInput, setDepartmentInput] = useState('');
  const [jobFunctionInput, setJobFunctionInput] = useState('');
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [checkingIdentity, setCheckingIdentity] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  
  // Nouveaux états pour les documents PDF
  const [identityDocFile, setIdentityDocFile] = useState<File | null>(null);
  const [birthCertFile, setBirthCertFile] = useState<File | null>(null);
  const [otherDocsFile, setOtherDocsFile] = useState<File | null>(null);

  // Gestionnaire de changement pour les champs
  const handleInputChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const value = e.target.type === 'number' 
      ? parseFloat(e.target.value) || 0 
      : e.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Charger compagnies et départements (uniquement si authentifié)
  useEffect(() => {
    let mounted = true;
    async function loadMeta() {
      try {
        setLoadingMeta(true);
        // Ne pas appeler l'API sans jeton: évite des 401 sur la page de login
        const access = (() => {
          try { return localStorage.getItem('access'); } catch { return null; }
        })();
        if (!access) {
          if (mounted) setLoadingMeta(false);
          return;
        }
        const [cs, ds, jfs] = await Promise.all([fetchCompanies(), fetchDepartments(), fetchJobFunctions()]);
        if (mounted) {
          setCompanies(cs);
          setDepartments(ds);
          setJobFunctions(jfs);
        }
      } catch (e) {
        console.error('Erreur de chargement des référentiels', e);
      } finally {
        if (mounted) setLoadingMeta(false);
      }
    }
    loadMeta();
    return () => {
      mounted = false;
    };
  }, []);

  // Pré-remplir le formulaire en mode édition
  useEffect(() => {
    if (mode === 'edit' && employeeData) {
      setFormData(prev => ({
        ...prev,
        // Informations personnelles
        nomPrenom: employeeData.full_name || '',
        dateNaissance: employeeData.date_of_birth || '',
        lieuNaissance: employeeData.place_of_birth || '',
        nationalite: employeeData.nationality || '',
        pere: employeeData.father_name || '',
        mere: employeeData.mother_name || '',
        domicile: employeeData.address || '',
        naturePiece: (['CNI', 'PASSEPORT', 'PERMIS', 'AUTRE'].includes(employeeData.identity_type || '')
          ? employeeData.identity_type as any
          : 'CNI'),
        numeroPiece: employeeData.identity_number || '',
        dateEtablissementPiece: (employeeData as any).identity_issue_date || '',
        validitePiece: employeeData.identity_valid_until || '',
        numeroCNPS: employeeData.cnps_number || '',
        gender: (employeeData.gender as any) || 'M',
        phone: employeeData.phone || '',
        phoneEmergency: employeeData.phone_emergency || '',
        email: employeeData.email || '',
        
        // Détails du contrat
        typeContrat: (employeeData.contract_type as any) || 'CDI',
        dateSignature: employeeData.contract_signature_date || '',
        dateDebut: employeeData.contract_start_date || '',
        dateFin: employeeData.contract_end_date || '',
        jobFunctionId: (employeeData as any).job_function || '',
        categorieProfessionnelle: (employeeData.professional_category as any) || '',
        lieuTravail: employeeData.work_location || '',
        
        // Organisation et contact
        departementId: employeeData.department || '',
        societeId: employeeData.company || '',
        
        // Rémunération
        salaireMensuel: employeeData.base_salary || 0,
        primeTransport: employeeData.transport_allowance || 0,
        primeRendement: employeeData.performance_bonus || 0,
        primeCommunication: employeeData.communication_allowance || 0,
        surSalaire: employeeData.sur_salaire || 0,
        salaireCategoriel: employeeData.salaire_categoriel || 0,
        totalSalaireNet: ((): number => {
          const raw = employeeData.total_salary;
          if (raw === null || raw === undefined) return 0;
          const num = typeof raw === 'string' ? Number(raw) : raw;
          return isNaN(num) ? 0 : num;
        })(),
      }));

      // Pré-remplir la photo si disponible
      if (employeeData.photo) {
        setPhotoPreview(employeeData.photo);
      }
    }
  }, [mode, employeeData]);

  // Initialiser les inputs texte pour société et département
  useEffect(() => {
    if (formData.societeId && companies.length > 0) {
      const company = companies.find(c => c.id === formData.societeId);
      if (company) {
        setCompanyInput(company.name);
      }
    }
  }, [formData.societeId, companies]);

  useEffect(() => {
    if (formData.departementId && departments.length > 0) {
      const dept = departments.find(d => d.id === formData.departementId);
      if (dept) {
        setDepartmentInput(dept.name);
      }
    }
  }, [formData.departementId, departments]);

  useEffect(() => {
    if (formData.jobFunctionId && jobFunctions.length > 0) {
      const jf = jobFunctions.find(j => j.id === formData.jobFunctionId);
      if (jf) {
        setJobFunctionInput(jf.name);
      }
    }
  }, [formData.jobFunctionId, jobFunctions]);

  const handleEmailBlur = async () => {
    const email = formData.email.trim();
    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return; // Ne vérifier que si le format est valide
    try {
      setCheckingEmail(true);
      const { available, message } = await checkEmailAvailability(email);
      if (!available && message) {
        setErrors(prev => ({ ...prev, email: message }));
        toast.error(message, {
          icon: '📧',
          duration: 4000,
        });
      } else {
        setErrors(prev => {
          const { email, ...rest } = prev;
          return rest;
        });
      }
    } catch (e) {
      console.error('Erreur vérification email:', e);
    } finally {
      setCheckingEmail(false);
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setPhotoFile(null);
      setPhotoPreview(null);
      return;
    }

    // Validation: taille max 5MB, types image seulement
    if (file.size > 5 * 1024 * 1024) {
      const errorMsg = 'La photo ne doit pas dépasser 5 MB';
      setErrors(prev => ({ ...prev, photo: errorMsg }));
      toast.error(errorMsg, {
        icon: '📷',
        duration: 4000,
      });
      return;
    }
    if (!file.type.startsWith('image/')) {
      const errorMsg = 'Le fichier doit être une image';
      setErrors(prev => ({ ...prev, photo: errorMsg }));
      toast.error(errorMsg, {
        icon: '📷',
        duration: 4000,
      });
      return;
    }
    
    setPhotoFile(file);
    setErrors(prev => {
      const { photo, ...rest } = prev;
      return rest;
    });
    
    // Générer preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Gestionnaires pour les documents PDF
  const handlePdfChange = (
    setter: React.Dispatch<React.SetStateAction<File | null>>,
    fieldName: string
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setter(null);
      return;
    }

    // Validation: PDF uniquement, max 100MB
    if (file.type !== 'application/pdf') {
      const errorMsg = 'Le fichier doit être au format PDF';
      setErrors(prev => ({ ...prev, [fieldName]: errorMsg }));
      toast.error(errorMsg, {
        icon: '📄',
        duration: 4000,
      });
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      const errorMsg = 'Le fichier ne doit pas dépasser 100 MB';
      setErrors(prev => ({ ...prev, [fieldName]: errorMsg }));
      toast.error(errorMsg, {
        icon: '📄',
        duration: 4000,
      });
      return;
    }
    
    setter(file);
    setErrors(prev => {
      const { [fieldName]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleIdentityBlur = async () => {
    const identity = formData.numeroPiece.trim();
    if (!identity) return;
    try {
      setCheckingIdentity(true);
      const { available, message } = await checkIdentityAvailability(identity);
      if (!available && message) {
        setErrors(prev => ({ ...prev, numeroPiece: message }));
        toast.error(message, {
          icon: '🆔',
          duration: 4000,
        });
      } else {
        setErrors(prev => {
          const { numeroPiece, ...rest } = prev;
          return rest;
        });
      }
    } catch (e) {
      console.error('Erreur vérification identité:', e);
    } finally {
      setCheckingIdentity(false);
    }
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^\+?[0-9\s\-\(\)]{8,20}$/;

  // Soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Empêcher les soumissions multiples
    if (isSubmitting) {
      console.log('⏸️ Soumission déjà en cours, ignorée');
      return;
    }

    console.log('🚀 Formulaire soumis !');
    setIsSubmitting(true);
    setErrors({});

    // Validation basique côté client - SEULEMENT nom et prénoms obligatoires
    const newErrors: Record<string, string> = {};
    if (!formData.nomPrenom.trim()) newErrors.nomPrenom = 'Nom et prénoms requis';

    // Validations optionnelles (seulement si les champs sont remplis)
    if (formData.dateNaissance) {
      const birthDate = new Date(formData.dateNaissance);
      const today = new Date();
      if (birthDate > today) {
        newErrors.dateNaissance = 'La date de naissance ne peut pas être dans le futur';
      }
    }
    if (formData.phone && !phoneRegex.test(formData.phone)) newErrors.phone = 'Téléphone invalide';
    if (formData.email && !emailRegex.test(formData.email)) newErrors.email = 'Email invalide';
    if (formData.typeContrat === 'CDD' && !formData.dateFin) {
      // Le serveur refuse désormais un CDD sans date de fin (Employee.clean).
      // Le dire ici évite à l'utilisateur d'aller au bout du formulaire pour
      // récupérer une 400.
      newErrors.dateFin = 'Fin de contrat requise pour CDD';
    }
    if (formData.typeContrat === 'CDD' && formData.dateFin && formData.dateDebut) {
      if (new Date(formData.dateFin) <= new Date(formData.dateDebut)) {
        newErrors.dateFin = 'La date de fin doit être après la date de début';
      }
    }

    if (Object.keys(newErrors).length) {
      console.log('❌ Erreurs de validation:', newErrors);
      setErrors(newErrors);

      // Afficher un toast pour la première erreur
      const firstErrorMessage = Object.values(newErrors)[0];
      toast.error(firstErrorMessage, {
        icon: '⚠️',
        duration: 4000,
      });

      // Scroller vers la première erreur
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"], [aria-label*="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setIsSubmitting(false);
      return;
    }
    
    console.log('✅ Validation réussie, envoi au serveur...');

    // Transformer nomPrenom en last_name / first_name
    const [maybeLast, ...rest] = formData.nomPrenom.trim().split(/\s+/);
    const last_name = maybeLast?.toUpperCase() || '';
    const first_name = rest.join(' ').trim();

    try {
      const payload = {
        last_name,
        first_name: first_name || maybeLast,
        date_of_birth: formData.dateNaissance || null,
        place_of_birth: formData.lieuNaissance || null,
        gender: formData.gender || null,
        father_name: formData.pere || null,
        mother_name: formData.mere || null,

        identity_type: formData.naturePiece || null,
        identity_number: formData.numeroPiece || null,
        identity_issue_date: formData.dateEtablissementPiece || null,
        identity_valid_until: formData.validitePiece || null,

        nationality: formData.nationalite || null,
        address: formData.domicile || null,
        city: formData.city || null,

        phone: formData.phone || null,
        phone_emergency: formData.phoneEmergency || null,
        email: formData.email || null,

        // Envoyer directement la valeur (ID ou nom) sans forcer la conversion
        company: formData.societeId || null,
        department: formData.departementId || null,
        job_function: formData.jobFunctionId || null,
        professional_category: formData.categorieProfessionnelle || null,

        contract_type: formData.typeContrat || null,
        contract_start_date: formData.dateDebut || null,
        contract_end_date: formData.dateFin || null,
        contract_signature_date: formData.dateSignature || null,

        base_salary: formData.salaireMensuel ? Number(formData.salaireMensuel) : null,
        transport_allowance: formData.primeTransport ? Number(formData.primeTransport) : null,
        performance_bonus: formData.primeRendement ? Number(formData.primeRendement) : null,
        communication_allowance: formData.primeCommunication ? Number(formData.primeCommunication) : null,
        sur_salaire: formData.surSalaire ? Number(formData.surSalaire) : null,
        salaire_categoriel: formData.salaireCategoriel ? Number(formData.salaireCategoriel) : null,

        cnps_number: formData.numeroCNPS || null,
        work_location: formData.lieuTravail || null,
        status: 'ACTIF',
      } as const;

      let result;
      if (mode === 'edit' && employeeData) {
        result = await updateEmployee(
          employeeData.id,
          payload,
          photoFile,
          identityDocFile,
          birthCertFile,
          otherDocsFile
        );
      } else {
        result = await createEmployee(
          payload,
          photoFile,
          identityDocFile,
          birthCertFile,
          otherDocsFile
        );
      }

      if (mode === 'edit') {
        toast.success(`Employé ${result?.full_name || last_name} modifié avec succès !`, {
          icon: '✅',
          duration: 4000,
        });
        onSuccess?.();
      } else {
        // Réinitialisation du formulaire en mode création uniquement
        setFormData({
          nomPrenom: '',
          dateNaissance: '',
          lieuNaissance: '',
          nationalite: '',
          pere: '',
          mere: '',
          domicile: '',
          naturePiece: 'CNI',
          numeroPiece: '',
          dateEtablissementPiece: '',
          validitePiece: '',
          numeroCNPS: '',
          gender: 'M',
          phone: '',
          phoneEmergency: '',
          city: 'Abidjan',
          typeContrat: 'CDI',
          dateSignature: '',
          dateDebut: '',
          dateFin: '',
          jobFunctionId: '',
          categorieProfessionnelle: '',
          lieuTravail: '',
          departementId: '',
          societeId: '',
          email: '',
          salaireMensuel: 0,
          primeTransport: 0,
          primeRendement: 0,
          primeCommunication: 0,
          surSalaire: 0,
          salaireCategoriel: 0,
          totalSalaireNet: 0,
        });

        setPhotoFile(null);
        setPhotoPreview(null);
        setIdentityDocFile(null);
        setBirthCertFile(null);
        setOtherDocsFile(null);

        toast.success(`Employé ${result?.full_name || last_name} créé avec succès !`, {
          icon: '🎉',
          duration: 4000,
        });
      }
      
      console.log(mode === 'edit' ? 'Employé modifié:' : 'Nouvel employé:', result);
    } catch (err: any) {
      console.error(mode === 'edit' ? 'Erreur modification employé:' : 'Erreur création employé:', err);
      // Gestion des erreurs 400 du backend (DRF)
      const data = err?.response?.data;
      if (data && typeof data === 'object') {
        // Mapping des clés backend vers frontend
        const backendToFrontendMap: Record<string, string> = {
          'contract_end_date': 'dateFin',
          'contract_start_date': 'dateDebut',
          'contract_signature_date': 'dateSignature',
          'date_of_birth': 'dateNaissance',
          'place_of_birth': 'lieuNaissance',
          'identity_number': 'numeroPiece',
          'identity_type': 'naturePiece',
          'identity_issue_date': 'dateEtablissementPiece',
          'identity_valid_until': 'validitePiece',
          'base_salary': 'salaireMensuel',
          'transport_allowance': 'primeTransport',
          'performance_bonus': 'primeRendement',
          'communication_allowance': 'primeCommunication',
          'sur_salaire': 'surSalaire',
          'salaire_categoriel': 'salaireCategoriel',
          'cnps_number': 'numeroCNPS',
          'phone_emergency': 'phoneEmergency',
          'job_function': 'jobFunctionId',
          'professional_category': 'categorieProfessionnelle',
          'work_location': 'lieuTravail',
          'department': 'departementId',
          'company': 'societeId',
          'father_name': 'pere',
          'mother_name': 'mere',
          'address': 'domicile',
        };

        const apiErrors: Record<string, string> = {};
        Object.entries(data as Record<string, any>).forEach(([k, v]) => {
          // Utiliser le mapping ou la clé originale si pas de mapping
          const frontendKey = backendToFrontendMap[k] || k;
          if (Array.isArray(v)) apiErrors[frontendKey] = v.join(' ');
          else if (typeof v === 'string') apiErrors[frontendKey] = v;
          else apiErrors[frontendKey] = 'Champ invalide';
        });
        setErrors(apiErrors);

        // Afficher un toast pour la première erreur du serveur
        const firstErrorMessage = Object.values(apiErrors)[0];
        toast.error(firstErrorMessage, {
          icon: '❌',
          duration: 5000,
        });

        // Scroller vers la première erreur
        const firstErrorField = Object.keys(apiErrors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorField}"], [aria-label*="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        toast.error("Une erreur est survenue lors de l'enregistrement", {
          icon: '❌',
          duration: 5000,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Annulation
  const handleCancel = () => {
    if (mode === 'edit' && employeeData?.id) {
      // En mode édition, retourner à la fiche détail
      if (window.confirm('Voulez-vous vraiment annuler ? Toutes les modifications seront perdues.')) {
        navigate(`/employees/${employeeData.id}`);
      }
    } else {
      // En mode création, réinitialiser le formulaire
      if (window.confirm('Voulez-vous vraiment annuler ? Toutes les modifications seront perdues.')) {
        setFormData({
        // Informations personnelles
        nomPrenom: '',
        dateNaissance: '',
        lieuNaissance: '',
        nationalite: '',
        pere: '',
        mere: '',
        domicile: '',
        naturePiece: 'CNI',
        numeroPiece: '',
        dateEtablissementPiece: '',
        validitePiece: '',
        numeroCNPS: '',
        gender: 'M',
        phone: '',
        phoneEmergency: '',
        city: 'Abidjan',

        // Détails du contrat
        typeContrat: 'CDI',
        dateSignature: '',
        dateDebut: '',
        dateFin: '',
        jobFunctionId: '',
        categorieProfessionnelle: '',
        lieuTravail: '',

        // Organisation et contact
        departementId: '',
        societeId: '',
        email: '',

        // Rémunération
        salaireMensuel: 0,
        primeTransport: 0,
        primeRendement: 0,
        primeCommunication: 0,
        surSalaire: 0,
        salaireCategoriel: 0,
        totalSalaireNet: 0,
      });
      setPhotoFile(null);
      setPhotoPreview(null);
      }
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:gap-6">
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body p-4 sm:p-6">
          
          {/* Affichage des erreurs globales */}
          {Object.keys(errors).length > 0 && (
            <div className="alert alert-error shadow-lg mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="font-bold">Erreurs de validation</h3>
                <ul className="text-sm mt-2">
                  {Object.entries(errors).map(([field, message]) => (
                    <li key={field}>• {message}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          {/* Ajout de noValidate pour désactiver la validation HTML5 native et permettre l'affichage des messages custom (ex: Email invalide) */}
          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            
            {/* -------------------- Section 1: Informations Personnelles -------------------- */}
            <h3 className="text-base sm:text-lg font-semibold text-warning">Informations Personnelles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="form-control col-span-1 md:col-span-2">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">PHOTO</span></label>
                <input 
                  type="file" 
                  accept="image/*"
                  className="file-input file-input-bordered w-full" 
                  onChange={handlePhotoChange}
                />
                {errors.photo && <span className="text-error text-sm">{errors.photo}</span>}
                {photoPreview && (
                  <div className="mt-2 flex items-center gap-2">
                    <img src={photoPreview} alt="Aperçu" className="w-20 h-20 object-cover rounded-lg border" />
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      onClick={() => {
                        setPhotoFile(null);
                        setPhotoPreview(null);
                        setErrors(prev => {
                          const { photo, ...rest } = prev;
                          return rest;
                        });
                      }}
                    >
                      ✕ Supprimer
                    </button>
                  </div>
                )}
              </div>

              {/* Documents PDF */}
              <div className="form-control col-span-1 md:col-span-2 lg:col-span-4">
                <div className="divider text-xs sm:text-sm">📄 Documents PDF</div>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text text-xs sm:text-sm font-medium">Pièce d'identité</span>
                  <span className="label-text-alt text-xs">100MB max</span>
                </label>
                <input
                  type="file"
                  accept="application/pdf"
                  aria-label="Pièce d'identité (PDF)"
                  className="file-input file-input-bordered file-input-sm w-full"
                  onChange={handlePdfChange(setIdentityDocFile, 'identity_document')}
                />
                {errors.identity_document && (
                  <span className="text-error text-sm mt-1 block">{errors.identity_document}</span>
                )}
                {identityDocFile && !errors.identity_document && (
                  <span className="label-text-alt text-success mt-1 block">
                    ✓ {identityDocFile.name} ({(identityDocFile.size / 1024).toFixed(2)} KB)
                  </span>
                )}
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text text-xs sm:text-sm font-medium">Extrait de naissance</span>
                  <span className="label-text-alt text-xs">100MB max</span>
                </label>
                <input
                  type="file"
                  accept="application/pdf"
                  aria-label="Extrait de naissance (PDF)"
                  className="file-input file-input-bordered file-input-sm w-full"
                  onChange={handlePdfChange(setBirthCertFile, 'birth_certificate')}
                />
                {errors.birth_certificate && (
                  <span className="text-error text-sm mt-1 block">{errors.birth_certificate}</span>
                )}
                {birthCertFile && !errors.birth_certificate && (
                  <span className="label-text-alt text-success mt-1 block">
                    ✓ {birthCertFile.name} ({(birthCertFile.size / 1024).toFixed(2)} KB)
                  </span>
                )}
              </div>

              <div className="form-control col-span-1 md:col-span-2">
                <label className="label">
                  <span className="label-text text-xs sm:text-sm font-medium">Autres documents</span>
                  <span className="label-text-alt text-xs">100MB max</span>
                </label>
                <input
                  type="file"
                  accept="application/pdf"
                  aria-label="Autres documents (PDF)"
                  className="file-input file-input-bordered file-input-sm w-full"
                  onChange={handlePdfChange(setOtherDocsFile, 'other_documents')}
                />
                {errors.other_documents && (
                  <span className="text-error text-sm mt-1 block">{errors.other_documents}</span>
                )}
                {otherDocsFile && !errors.other_documents && (
                  <span className="label-text-alt text-success mt-1 block">
                    ✓ {otherDocsFile.name} ({(otherDocsFile.size / 1024).toFixed(2)} KB)
                  </span>
                )}
              </div>


              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Nom et prénoms</span></label>
                <input
                  type="text"
                  placeholder="Ex: DUPONT Jean"
                  className="input input-bordered input-sm sm:input-md w-full"
                  value={formData.nomPrenom}
                  onChange={handleInputChange('nomPrenom')}
                  required
                />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Sexe</span></label>
                <select
                  className="select select-bordered select-sm sm:select-md w-full"
                  aria-label="Sexe"
                  value={formData.gender}
                  onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value as 'M' | 'F' }))}
                >
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                </select>
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Date de naissance</span></label>
                <input
                  type="date"
                  className="input input-bordered input-sm sm:input-md w-full"
                  aria-label="Date de naissance"
                  value={formData.dateNaissance}
                  onChange={handleInputChange('dateNaissance')}
                />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Lieu de naissance</span></label>
                <input
                  type="text"
                  placeholder="Ex: Abidjan"
                  className="input input-bordered input-sm sm:input-md w-full"
                  aria-label="Lieu de naissance"
                  value={formData.lieuNaissance}
                  onChange={handleInputChange('lieuNaissance')}
                />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Nationalité</span></label>
                <select
                  className="select select-bordered select-sm sm:select-md w-full"
                  aria-label="Nationalité"
                  value={formData.nationalite}
                  onChange={handleInputChange('nationalite')}
                >
                  {AFRICAN_NATIONALITIES.map((nationality) => (
                    <option key={nationality} value={nationality}>
                      {nationality}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Père</span></label>
                <input
                  type="text"
                  placeholder="Nom du père"
                  className="input input-bordered input-sm sm:input-md w-full"
                  value={formData.pere}
                  onChange={handleInputChange('pere')}
                />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Mère</span></label>
                <input
                  type="text"
                  placeholder="Nom de la mère"
                  className="input input-bordered input-sm sm:input-md w-full"
                  value={formData.mere}
                  onChange={handleInputChange('mere')}
                />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Domicile</span></label>
                <input
                  type="text"
                  placeholder="Adresse"
                  className="input input-bordered input-sm sm:input-md w-full"
                  value={formData.domicile}
                  onChange={handleInputChange('domicile')}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 pt-4 border-t border-base-200">
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Type de pièce</span></label>
                <select
                  className="select select-bordered select-sm sm:select-md w-full"
                  value={formData.naturePiece}
                  onChange={(e) => setFormData(prev => ({ ...prev, naturePiece: e.target.value as any }))}
                >
                  {IDENTITY_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text text-xs sm:text-sm font-medium">N° pièce</span>
                  {checkingIdentity && <span className="loading loading-spinner loading-xs"></span>}
                </label>
                <input
                  type="text"
                  placeholder="Numéro"
                  className={`input input-bordered input-sm sm:input-md w-full ${errors.numeroPiece ? 'input-error' : ''}`}
                  aria-label="Numéro de pièce"
                  value={formData.numeroPiece}
                  onChange={handleInputChange('numeroPiece')}
                  onBlur={handleIdentityBlur}
                />
                {errors.numeroPiece && <span className="text-error text-xs">{errors.numeroPiece}</span>}
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Date établissement</span></label>
                <input
                  type="date"
                  className="input input-bordered input-sm sm:input-md w-full"
                  aria-label="Date établissement"
                  value={formData.dateEtablissementPiece}
                  onChange={handleInputChange('dateEtablissementPiece')}
                />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Validité</span></label>
                <input
                  type="date"
                  className="input input-bordered input-sm sm:input-md w-full"
                  aria-label="Validité"
                  value={formData.validitePiece}
                  onChange={handleInputChange('validitePiece')}
                />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">N° CNPS</span></label>
                <input
                  type="text"
                  placeholder="Numéro CNPS"
                  className="input input-bordered input-sm sm:input-md w-full"
                  value={formData.numeroCNPS}
                  onChange={handleInputChange('numeroCNPS')}
                />
              </div>
            </div>
            
            <div className="divider"></div>

            {/* -------------------- Section 2: Détails du Contrat et Rémunération -------------------- */}
            <h3 className="text-base sm:text-lg font-semibold text-warning">Contrat et Poste</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Type de contrat</span></label>
                <select
                  className="select select-bordered select-sm sm:select-md w-full"
                  aria-label="Type de contrat"
                  value={formData.typeContrat}
                  onChange={(e) => setFormData(prev => ({ ...prev, typeContrat: e.target.value as any }))}
                >
                  <option value="CDI">CDI</option>
                  <option value="CDD">CDD</option>
                  <option value="STAGE">Stage</option>
                  <option value="PRESTATION">Prestation</option>
                </select>
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Date signature</span></label>
                <input
                  type="date"
                  className="input input-bordered input-sm sm:input-md w-full"
                  aria-label="Date signature"
                  value={formData.dateSignature}
                  onChange={handleInputChange('dateSignature')}
                />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Date début</span></label>
                <input
                  type="date"
                  className="input input-bordered input-sm sm:input-md w-full"
                  aria-label="Date début"
                  value={formData.dateDebut}
                  onChange={handleInputChange('dateDebut')}
                />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Date fin</span></label>
                <input
                  type="date"
                  className="input input-bordered input-sm sm:input-md w-full"
                  aria-label="Date fin"
                  value={formData.dateFin}
                  onChange={handleInputChange('dateFin')}
                />
                {errors.dateFin && <span className="text-error text-xs">{errors.dateFin}</span>}
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text text-xs sm:text-sm font-medium">Fonction</span>
                </label>
                <input
                  list="job-functions-list"
                  className={`input input-bordered input-sm sm:input-md w-full ${errors.jobFunctionId ? 'input-error' : ''}`}
                  aria-label="Fonction"
                  value={jobFunctionInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setJobFunctionInput(value);

                    // Chercher une fonction correspondante par nom
                    const matchedJF = jobFunctions.find(jf => jf.name === value);
                    if (matchedJF) {
                      setFormData(prev => ({ ...prev, jobFunctionId: matchedJF.id }));
                    } else {
                      // Nouvelle saisie - stocker le nom comme texte
                      setFormData(prev => ({ ...prev, jobFunctionId: value as any }));
                    }
                  }}
                  placeholder={loadingMeta ? 'Chargement…' : 'Sélectionner ou saisir une fonction'}
                />
                <datalist id="job-functions-list">
                  {jobFunctions.map(jf => (
                    <option key={jf.id} value={jf.name} />
                  ))}
                </datalist>
                {errors.jobFunctionId && <span className="text-error text-xs">{errors.jobFunctionId}</span>}
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Catégorie</span></label>
                <select
                  className="select select-bordered select-sm sm:select-md w-full"
                  aria-label="Catégorie professionnelle"
                  value={formData.categorieProfessionnelle}
                  onChange={(e) => setFormData(prev => ({ ...prev, categorieProfessionnelle: e.target.value as any }))}
                >
                  <option value="" disabled>Sélectionner</option>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.categorieProfessionnelle && <span className="text-error text-xs">{errors.categorieProfessionnelle}</span>}
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Lieu de travail</span></label>
                <input
                  type="text"
                  placeholder="Ex: Abidjan"
                  className="input input-bordered input-sm sm:input-md w-full"
                  value={formData.lieuTravail}
                  onChange={handleInputChange('lieuTravail')}
                />
              </div>
            </div>

            <div className="divider"></div>

            {/* -------------------- Section 3: Organisation et Contact -------------------- */}
            <h3 className="text-base sm:text-lg font-semibold text-warning">Organisation et Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text text-xs sm:text-sm font-medium">Société</span>
                  {loadingMeta && <span className="label-text-alt"><span className="loading loading-spinner loading-xs" /></span>}
                </label>
                <input
                  list="companies-list"
                  className={`input input-bordered input-sm sm:input-md w-full ${errors.societeId ? 'input-error' : ''}`}
                  aria-label="Société"
                  value={companyInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCompanyInput(value);

                    // Chercher une société correspondante par nom
                    const matchedCompany = companies.find(c => c.name === value);
                    if (matchedCompany) {
                      setFormData(prev => ({ ...prev, societeId: matchedCompany.id }));
                    } else {
                      // Nouvelle saisie - stocker le nom comme texte
                      setFormData(prev => ({ ...prev, societeId: value as any }));
                    }
                  }}
                  placeholder={loadingMeta ? 'Chargement…' : 'Sélectionner ou saisir une société'}
                />
                <datalist id="companies-list">
                  {companies.map(c => (
                    <option key={c.id} value={c.name} />
                  ))}
                </datalist>
                {errors.societeId && <span className="text-error text-xs">{errors.societeId}</span>}
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text text-xs sm:text-sm font-medium">Département</span>
                  {loadingMeta && <span className="label-text-alt"><span className="loading loading-spinner loading-xs" /></span>}
                </label>
                <input
                  list="departments-list"
                  className={`input input-bordered input-sm sm:input-md w-full ${errors.departementId ? 'input-error' : ''}`}
                  aria-label="Département"
                  value={departmentInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDepartmentInput(value);

                    // Chercher un département correspondant par nom
                    const matchedDept = departments.find(d => d.name === value);
                    if (matchedDept) {
                      setFormData(prev => ({ ...prev, departementId: matchedDept.id }));
                    } else {
                      // Nouvelle saisie - stocker le nom comme texte
                      setFormData(prev => ({ ...prev, departementId: value as any }));
                    }
                  }}
                  placeholder={loadingMeta ? 'Chargement…' : 'Sélectionner ou saisir un département'}
                />
                <datalist id="departments-list">
                  {departments.map(d => (
                    <option key={d.id} value={d.name} />
                  ))}
                </datalist>
                {errors.departementId && <span className="text-error text-xs">{errors.departementId}</span>}
              </div>
              <div className="form-control col-span-1 md:col-span-2">
                <label className="label">
                  <span className="label-text text-xs sm:text-sm font-medium">Email</span>
                  {checkingEmail && <span className="loading loading-spinner loading-xs"></span>}
                </label>
                <input
                  type="email"
                  placeholder="email@entreprise.com"
                  className={`input input-bordered input-sm sm:input-md w-full ${errors.email ? 'input-error' : ''}`}
                  aria-label="Email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  onBlur={handleEmailBlur}
                />
                {errors.email && <span className="text-error text-xs">{errors.email}</span>}
              </div>
              <div className="form-control col-span-1 md:col-span-2">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Téléphone</span></label>
                <input
                  type="tel"
                  placeholder="+225 07 00 00 00"
                  className={`input input-bordered input-sm sm:input-md w-full ${errors.phone ? 'input-error' : ''}`}
                  aria-label="Téléphone"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                />
                {errors.phone && <span className="text-error text-xs">{errors.phone}</span>}
              </div>
              <div className="form-control col-span-1 md:col-span-2">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Tél. urgence</span></label>
                <input
                  type="tel"
                  placeholder="+225 05 00 00 00"
                  className="input input-bordered input-sm sm:input-md w-full"
                  value={formData.phoneEmergency}
                  onChange={handleInputChange('phoneEmergency')}
                />
              </div>
            </div>

            <div className="divider"></div>

            {/* -------------------- Section 4: Rémunération -------------------- */}
            <h3 className="text-base sm:text-lg font-semibold text-warning">Rémunération</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Salaire brut</span></label>
                <input
                  type="number"
                  placeholder="0"
                  className="input input-bordered input-sm sm:input-md w-full"
                  aria-label="Salaire brut"
                  value={formData.salaireMensuel || ''}
                  onChange={handleInputChange('salaireMensuel')}
                />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Prime transport</span></label>
                <input
                  type="number"
                  placeholder="0"
                  className="input input-bordered input-sm sm:input-md w-full"
                  value={formData.primeTransport || ''}
                  onChange={handleInputChange('primeTransport')}
                />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Prime rendement</span></label>
                <input
                  type="number"
                  placeholder="0"
                  className="input input-bordered input-sm sm:input-md w-full"
                  value={formData.primeRendement || ''}
                  onChange={handleInputChange('primeRendement')}
                />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Prime communication</span></label>
                <input
                  type="number"
                  placeholder="0"
                  className="input input-bordered input-sm sm:input-md w-full"
                  value={formData.primeCommunication || ''}
                  onChange={handleInputChange('primeCommunication')}
                />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Sur salaire</span></label>
                <input
                  type="number"
                  placeholder="0"
                  className="input input-bordered input-sm sm:input-md w-full"
                  aria-label="Sur salaire"
                  value={formData.surSalaire || ''}
                  onChange={handleInputChange('surSalaire')}
                />
              </div>
              
              <div className="form-control">
                <label className="label"><span className="label-text text-xs sm:text-sm font-medium">Salaire catégoriel</span></label>
                <input
                  type="number"
                  placeholder="0"
                  className="input input-bordered input-sm sm:input-md w-full"
                  aria-label="Salaire catégoriel"
                  value={formData.salaireCategoriel || ''}
                  onChange={handleInputChange('salaireCategoriel')}
                />
              </div>
            </div>
            
            <div className="card-actions justify-end pt-8 border-t mt-8">
              <button type="button" className="btn btn-ghost" onClick={handleCancel}>
                Annuler
              </button>
              <button
                type="submit"
                className="btn btn-warning"
                disabled={isSubmitting}
              >
                {isSubmitting && <span className="loading loading-spinner loading-sm"></span>}
                {isSubmitting ? 'Enregistrement...' : "Enregistrer l'Employé"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmployeeForm;